import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, Maximize2, MoveHorizontal, X, ZoomIn } from "lucide-react";
import { DemoCard, useGuide } from "./Guide";
import { COMICS, MERCH, NYC, NYC_LABELS, STREET } from "./assets";

export function CarouselDemo() {
    const { say } = useGuide();
    const [i, setI] = useState(0);
    const go = (d) => {
        setI((x) => (x + d + NYC.length) % NYC.length);
        say(`Classic carousel — arrows, captions, infinite loop. That's ${NYC_LABELS[(i + d + NYC.length) % NYC.length]}.`, "carousel");
    };
    return (
        <DemoCard title="Arrow carousel" hint="click arrows" span="lg:col-span-2" testid="demo-carousel">
            <div className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-white/10">
                <AnimatePresence mode="popLayout">
                    <motion.img key={i} src={NYC[i]} alt={NYC_LABELS[i]} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }} className="absolute inset-0 h-full w-full object-cover" />
                </AnimatePresence>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/85 to-transparent p-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-200">{NYC_LABELS[i]}</span>
                    <span className="flex gap-2">
                        <button onClick={() => go(-1)} className="rounded-full bg-black/70 p-2 hover:bg-brand-green hover:text-black" data-testid="demo-carousel-prev"><ChevronLeft size={15} /></button>
                        <button onClick={() => go(1)} className="rounded-full bg-black/70 p-2 hover:bg-brand-green hover:text-black" data-testid="demo-carousel-next"><ChevronRight size={15} /></button>
                    </span>
                </div>
            </div>
        </DemoCard>
    );
}

export function SwipeStripDemo() {
    const { say } = useGuide();
    const ref = useRef(null);
    return (
        <DemoCard title="Swipe filmstrip" hint="drag / swipe" testid="demo-swipe">
            <div ref={ref} onScroll={() => say("Swipeable strips are how phones want to browse. Thumb-friendly, no arrows needed.", "swipe")}
                className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[...STREET, ...MERCH].map((src, i) => (
                    <img key={i} src={src} alt="" className="h-32 w-44 shrink-0 snap-start rounded-xl object-cover" loading="lazy" />
                ))}
            </div>
        </DemoCard>
    );
}

export function LightboxDemo() {
    const { say } = useGuide();
    const [open, setOpen] = useState(null);
    return (
        <DemoCard title="Lightbox gallery" hint="tap a photo" testid="demo-lightbox">
            <div className="grid grid-cols-3 gap-2">
                {NYC.slice(0, 6).map((src, i) => (
                    <button key={i} onClick={() => { setOpen(i); say("Full-screen lightbox — portfolio, menu, before/after shots. Never leaves the page.", "lightbox"); }}
                        className="group relative aspect-square overflow-hidden rounded-lg" data-testid={`demo-lightbox-${i}`}>
                        <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"><Expand size={16} /></span>
                    </button>
                ))}
            </div>
            <AnimatePresence>
                {open !== null && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-6" onClick={() => setOpen(null)} data-testid="demo-lightbox-overlay">
                        <motion.img initial={{ scale: 0.9 }} animate={{ scale: 1 }} src={NYC[open]} alt="" className="max-h-[80vh] rounded-2xl" />
                        <button className="absolute right-6 top-6 rounded-full bg-white/10 p-2.5"><X size={18} /></button>
                    </motion.div>
                )}
            </AnimatePresence>
        </DemoCard>
    );
}

export function BeforeAfterDemo() {
    const { say } = useGuide();
    const [v, setV] = useState(50);
    return (
        <DemoCard title="Before / after slider" hint="drag the handle" testid="demo-before-after">
            <div className="relative aspect-[4/3] select-none overflow-hidden rounded-2xl border border-white/10">
                <img src={NYC[3]} alt="after" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 overflow-hidden" style={{ width: `${v}%` }}>
                    <img src={NYC[3]} alt="before" className="h-full w-full object-cover grayscale" style={{ width: `${10000 / v}%`, maxWidth: "none" }} />
                </div>
                <div className="absolute inset-y-0 flex w-1 items-center justify-center bg-brand-green" style={{ left: `${v}%` }}>
                    <span className="rounded-full bg-brand-green p-1.5 text-black"><MoveHorizontal size={13} /></span>
                </div>
                <input type="range" min="4" max="96" value={v} onChange={(e) => { setV(+e.target.value); }} onMouseUp={() => say("Before/after sliders sell renovation, detailing, restoration, print quality — anything with a 'wow, that's the same photo?' moment.", "beforeafter")}
                    className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0" data-testid="demo-before-after-range" />
            </div>
        </DemoCard>
    );
}

export function MasonryDemo() {
    const { say } = useGuide();
    return (
        <DemoCard title="Masonry wall" hint="hover a tile" span="lg:col-span-2" testid="demo-masonry">
            <div className="columns-2 gap-3 sm:columns-3" onMouseEnter={() => say("Masonry grid — every photo keeps its own shape. Great for merch, tattoos, food, real estate.", "masonry")}>
                {[...NYC, ...STREET].slice(0, 6).map((src, i) => (
                    <img key={i} src={src} alt="" loading="lazy"
                        className={`mb-3 w-full rounded-xl object-cover transition-all duration-500 hover:brightness-125 ${i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/3]"}`} />
                ))}
            </div>
        </DemoCard>
    );
}

export function HoverZoomDemo() {
    const { say } = useGuide();
    const [z, setZ] = useState({ on: false, x: 50, y: 50 });
    return (
        <DemoCard title="Zoom on hover" hint="hover the print" testid="demo-hover-zoom">
            <div onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setZ({ on: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
                onMouseLeave={() => setZ({ ...z, on: false })}
                onMouseEnter={() => say("Magnifier zoom, like the big retailers. Show off stitching, ink, texture.", "zoom")}
                className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/10">
                <img src={MERCH[0]} alt="" className="h-full w-full object-cover transition-transform duration-300"
                    style={{ transform: z.on ? "scale(2)" : "scale(1)", transformOrigin: `${z.x}% ${z.y}%` }} />
                <span className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-brand-green"><ZoomIn size={14} /></span>
            </div>
        </DemoCard>
    );
}

export function ComicStripDemo() {
    const { say } = useGuide();
    const [panel, setPanel] = useState(0);
    const lines = [
        "PANEL 1 — Our hero needs a website. The quote says $40,000 and six months.",
        "PANEL 2 — He texts 13th Ave instead. 'Seven days,' we say.",
        "PANEL 3 — Day 7. Live URL. Payments working. He's taking orders in his sleep.",
        "PANEL 4 — THE END? No — he came back for shirts, signs and a second site.",
    ];
    return (
        <DemoCard title="Comic-strip storytelling" hint="click the panel" span="lg:col-span-2" testid="demo-comic">
            <div className="grid gap-3 sm:grid-cols-2">
                <button onClick={() => { const n = (panel + 1) % lines.length; setPanel(n); say("Storytelling panels — comics, step-by-steps, 'how it works' strips. Nobody scrolls past a story.", "comic"); }}
                    className="relative overflow-hidden rounded-2xl border-2 border-white/20" data-testid="demo-comic-btn">
                    <img src={COMICS[panel]} alt="" className="h-full max-h-56 w-full object-cover" />
                    <span className="absolute bottom-0 left-0 right-0 bg-black/80 p-2 font-mono text-[11px] uppercase tracking-widest text-brand-green">panel {panel + 1} / {lines.length} · tap for next</span>
                </button>
                <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                    <p className="font-display text-lg font-black leading-snug">{lines[panel]}</p>
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">your story, four panels, one scroll</p>
                </div>
            </div>
        </DemoCard>
    );
}

export function ParallaxDemo() {
    const { say } = useGuide();
    const [y, setY] = useState(0);
    return (
        <DemoCard title="Parallax layers" hint="scroll inside" testid="demo-parallax">
            <div onScroll={(e) => { setY(e.currentTarget.scrollTop); say("Parallax depth — background moves slower than the front. Feels like a movie title card.", "parallax"); }}
                className="h-32 overflow-y-auto rounded-2xl border border-white/10">
                <div className="relative h-64">
                    <img src={NYC[4]} alt="" className="sticky top-0 h-32 w-full object-cover" style={{ transform: `translateY(${y * -0.25}px)` }} />
                    <p className="relative -mt-10 px-4 font-display text-xl font-black">Brooklyn, in layers.</p>
                    <p className="px-4 pt-24 font-mono text-[11px] uppercase tracking-widest text-zinc-500">keep scrolling inside this box</p>
                </div>
            </div>
        </DemoCard>
    );
}

export function FullscreenHeroDemo() {
    const { say } = useGuide();
    const [dark, setDark] = useState(true);
    return (
        <DemoCard title="Hero, two moods" hint="flip the theme" testid="demo-hero-mood">
            <div className={`relative overflow-hidden rounded-2xl border p-5 transition-colors duration-500 ${dark ? "border-white/10 bg-black" : "border-black/10 bg-zinc-100"}`}>
                <img src={NYC[0]} alt="" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${dark ? "opacity-30" : "opacity-15"}`} />
                <div className="relative">
                    <p className={`font-display text-xl font-black ${dark ? "text-white" : "text-black"}`}>Your name, 60px tall.</p>
                    <p className={`mt-1 text-xs ${dark ? "text-zinc-400" : "text-zinc-600"}`}>Same site. Different mood. Pick one — or let visitors pick.</p>
                    <button onClick={() => { setDark(!dark); say("Light mode, dark mode, or a switch your customers control. Your call.", "mood"); }}
                        className={`mt-3 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-widest ${dark ? "bg-brand-green text-black" : "bg-black text-white"}`} data-testid="demo-hero-mood-btn">
                        {dark ? "Try light" : "Try dark"}
                    </button>
                </div>
            </div>
        </DemoCard>
    );
}

export function StackedCardsDemo() {
    const { say } = useGuide();
    const [top, setTop] = useState(0);
    return (
        <DemoCard title="Stacked deck" hint="deal a card" testid="demo-stack">
            <div className="relative h-32" onClick={() => { setTop((t) => t + 1); say("Card decks for testimonials, packages, drops. One at a time keeps attention.", "stack"); }}>
                {MERCH.map((src, i) => {
                    const off = (i - top + MERCH.length) % MERCH.length;
                    return (
                        <motion.img key={i} src={src} alt="" animate={{ y: off * 10, scale: 1 - off * 0.06, zIndex: 10 - off, opacity: off > 2 ? 0 : 1 }}
                            className="absolute left-1/2 h-28 w-40 -translate-x-1/2 rounded-xl border border-white/15 object-cover" data-testid={`demo-stack-card-${i}`} />
                    );
                })}
                <span className="absolute bottom-0 left-0 font-mono text-[11px] uppercase tracking-widest text-zinc-500">tap the deck</span>
            </div>
        </DemoCard>
    );
}

export function VerticalFeedDemo() {
    const { say } = useGuide();
    return (
        <DemoCard title="Reels-style feed" hint="scroll it" testid="demo-feed">
            <div onScroll={() => say("A vertical snap feed — TikTok scrolling, but on your own site where you keep the customer.", "feed")}
                className="h-40 snap-y snap-mandatory overflow-y-auto rounded-2xl border border-white/10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[...MERCH, ...STREET].map((src, i) => (
                    <div key={i} className="relative h-40 snap-start">
                        <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                        <span className="absolute bottom-2 left-2 rounded-full bg-black/70 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-white">drop {i + 1} · swipe up</span>
                    </div>
                ))}
            </div>
        </DemoCard>
    );
}

export function FullBleedDemo() {
    const { say } = useGuide();
    return (
        <DemoCard title="Edge-to-edge banner" hint="hover" testid="demo-fullbleed">
            <div onMouseEnter={() => say("Full-bleed imagery with text on top. Instant premium — this is how the big brands open a page.", "fullbleed")}
                className="group relative h-28 overflow-hidden rounded-2xl">
                <img src={STREET[3]} alt="" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/85 to-transparent p-4">
                    <p className="font-display text-lg font-black uppercase tracking-tight">Open late. Print faster.</p>
                </div>
                <span className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-brand-green"><Maximize2 size={13} /></span>
            </div>
        </DemoCard>
    );
}
